import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AdvanceQualificationMethod,
  LendingPaymentSource,
  LendingPaymentSourceDisplayGroup,
  LendingPaymentTypeInput,
  PlaidEntryPointInput,
} from '@varo/graphql-schema-types';
import { endOfDay, parseISO, startOfDay } from 'date-fns';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import {
  isNotQualified,
  isQualified,
} from 'features/advance/helpers/AdvanceProducts.helpers';
import useAdvancePaymentAccountController from 'features/advance/hooks/useAdvancePaymentAccountController/useAdvancePaymentAccountController';
import useVaroAdvanceProducts from 'features/advance/hooks/useGetVaroAdvanceProducts';
import AdvanceAccountListBottomSheet from 'features/advance/screens/registration/AutoPaymentScreen/AdvanceAccountListBottomSheet';
import { formatAccountName } from 'features/advance/screens/registration/AutoPaymentScreen/AdvanceAccountListBottomSheet.helpers';
import { autoPaymentDateSchema } from 'features/advance/screens/registration/AutoPaymentScreen/AutoPaymentDate.schema';
import { TabaPayEntryFlowType } from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BackHandler, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import CalendarPicker from 'shared/components/vxl/CalendarPicker/CalendarPicker';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { formatDate } from 'shared/utils/string/date';
import {
  showZendeskArticle,
  zendeskArticleIds,
} from 'shared/utils/zendesk/zendeskUtils';

function AutoPaymentChooseScreen() {
  const router = useRouter();
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState<{
    displayDate: string;
    calculatedDate: Date | null;
  }>({
    displayDate: '',
    calculatedDate: null,
  });
  const [dateError, setDateError] = useState('');
  const [isValidData, setIsValid] = useState(false);
  const [openAddDebitCard, setOpenAddDebitCard] = useState(false);

  const { bottom } = useSafeAreaInsets();

  const { t: translatedText } = useTranslation('advance');

  const {
    data: advanceProductsData,
    fetching: fetchingAdvanceProducts,
    error: advanceProductsError,
  } = useVaroAdvanceProducts('cache-and-network');

  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const qualifiedData = isQualified(advanceProductsData)
    ? advanceProductsData
    : null;

  const notQualified = isNotQualified(advanceProductsData)
    ? advanceProductsData
    : null;

  const {
    selectedAmount,
    feeAmount,
    optionId: amountId,
    fromDebitCardFlow,
    qualificationMethod,
  } = useLocalSearchParams<{
    selectedAmount: string;
    feeAmount: string;
    optionId: string;
    fromDebitCardFlow?: string;
    qualificationMethod: AdvanceQualificationMethod;
  }>();

  const {
    openPlaid: openPlaidLink,
    loading: isPolling,
    paymentOptions: accountOptionsData,
    selectedAccount,
    setSelectedAccount,
    refetch: refetchAutoPaymentOptions,
    error: accountOptionsError,
  } = useAdvancePaymentAccountController({
    paymentType: LendingPaymentTypeInput.Auto,
    plaidEntryPoint: PlaidEntryPointInput.Advance,
  });

  const canContinue =
    !!selectedDate && !!selectedAccount && !dateError.length && isValidData;

  const showBottomSheet = () => {
    if (accountOptionsData) {
      bottomSheetRef?.current?.present();
    }
  };

  const title = showCalendar ? translatedText('autopayment.title') : '';

  useEffect(() => {
    if (openAddDebitCard) {
      router.navigate({
        pathname: '/move-money/tabapay/dcf-tabapay-form',
        params: {
          tabaPayEntryFlowType: TabaPayEntryFlowType.AutoPay,
          advanceAmount: selectedAmount,
          feeAmount: feeAmount,
          amountId,
          qualificationMethod,
        },
      });
      setOpenAddDebitCard(false);
    }
  }, [
    feeAmount,
    amountId,
    openAddDebitCard,
    selectedAmount,
    qualificationMethod,
    router,
  ]);

  const hasFocusedBefore = useRef(false);

  useFocusEffect(
    useCallback(() => {
      if (hasFocusedBefore.current) {
        refetchAutoPaymentOptions();
      } else {
        hasFocusedBefore.current = true;
      }
    }, [refetchAutoPaymentOptions]),
  );

  const { showLinkDebitCard, showLinkAccount } = accountOptionsData || {};

  useEffect(() => {
    if (
      !accountOptionsData ||
      selectedAccount ||
      fromDebitCardFlow !== 'true'
    ) {
      return;
    }

    const debitCardGroup = accountOptionsData.options.find(
      (option) =>
        option.displayGroup === LendingPaymentSourceDisplayGroup.DebitCard,
    );
    const externalDebitCardDetail = debitCardGroup?.details.find(
      (detail) => detail.source === LendingPaymentSource.ExternalDebitCard,
    );

    if (externalDebitCardDetail) {
      setSelectedAccount(externalDebitCardDetail);
    }
  }, [
    accountOptionsData,
    fromDebitCardFlow,
    selectedAccount,
    setSelectedAccount,
  ]);

  const hasRequiredUrlParams = Boolean(
    selectedAmount && feeAmount && amountId && qualificationMethod,
  );

  const repaymentDates = qualifiedData?.repaymentDates;

  const logFAQClickedAnalytics = usePressableAnalytics({
    analyticsId: 'faqsClicked',
    customAttributes: {
      faq_name: 'va_payment',
    },
  });

  const logEventCalendarIconPressed = usePressableAnalytics({
    analyticsId: 'calendarClicked',
  });

  const handleZendeskArticle = () => {
    logFAQClickedAnalytics();
    try {
      showZendeskArticle(zendeskArticleIds.ADVANCE_REPAYMENT);
    } catch (error) {
      console.error('Error showing Zendesk article:', error);
    }
  };

  const {
    dueDate: DUE_DATE,
    max: MAX_DATE,
    min: MIN_DATE,
  } = repaymentDates ?? {};

  const formattedMaxDate = MAX_DATE
    ? formatDate(parseISO(MAX_DATE), 'MMM dd, yyyy')
    : '';
  const formattedMinDate = MIN_DATE
    ? formatDate(parseISO(MIN_DATE), 'MMM dd, yyyy')
    : '';
  const formattedDueDate = DUE_DATE
    ? formatDate(parseISO(DUE_DATE), 'MMM dd, yyyy')
    : '';
  const formattedPaymentDueDate = DUE_DATE
    ? formatDate(parseISO(DUE_DATE), 'MMM dd')
    : '';

  const handleDateSelection = (date: string) => {
    const isoDate = parseISO(date);
    handleValidate(isoDate);
  };

  const handleValidate = (isoDate: Date) => {
    const formattedDate = formatDate(isoDate);
    setSelectedDate((previousValue) => ({
      ...previousValue,
      displayDate: formattedDate,
      calculatedDate: isoDate,
    }));

    const minDateAltFormatted = MIN_DATE
      ? formatDate(parseISO(MIN_DATE), 'yyyy-MM-dd')
      : '';
    const maxDateAltFormatted = MAX_DATE
      ? formatDate(parseISO(MAX_DATE), 'yyyy-MM-dd')
      : '';

    const beginningOfDateMinDate = startOfDay(parseISO(minDateAltFormatted));
    const endOfDayMaxDate = endOfDay(parseISO(maxDateAltFormatted));

    const valid = autoPaymentDateSchema(
      new Date(beginningOfDateMinDate),
      new Date(endOfDayMaxDate),
      repaymentDates?.valid || [],
      repaymentDates?.exclusionReason || '',
    ).safeParse(isoDate);

    setIsValid(valid.success);

    setDateError(valid?.error?.issues[0].message ?? '');
  };
  const handleContinuePress = () => {
    if (!selectedDate.calculatedDate || !selectedAccount) {
      setIsValid(false);
      return;
    }

    handleValidate(selectedDate.calculatedDate);

    const repaymentDate = formatDate(selectedDate.calculatedDate, 'yyyy-MM-dd');

    if (isValidData) {
      router.push({
        pathname: '/varo-advance/autopayment-authorization',
        params: {
          qualificationMethod,
          advanceAmount: selectedAmount,
          dueDate: DUE_DATE,
          feeAmount,
          amountId,
          repaymentDate,
          selectedAccountId: selectedAccount?.accountId,
          paymentSource: selectedAccount?.source,
          selectedAccountName: formatAccountName(
            selectedAccount.name,
            selectedAccount.maskedAccountNumber || '',
          ),
        },
      });
    } else {
      router.navigate({
        pathname: '/varo-advance/error',
      });
    }
  };

  const handleBackPress = useCallback(() => {
    // if calendar is open then hide calendar
    if (!fetchingAdvanceProducts && showCalendar) {
      setShowCalendar(false);
    } else {
      if (router.canGoBack()) {
        router.back();
      } else {
        router.replace('/varo-advance/new');
      }
    }
    return null;
  }, [fetchingAdvanceProducts, showCalendar, router]);

  useFocusEffect(
    useCallback(() => {
      const backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => backHandler.remove();
    }, [handleBackPress]),
  );

  const isLoading = fetchingAdvanceProducts || isPolling;

  if (notQualified) {
    return <Redirect href="/varo-advance/qualification" />;
  } else if (!hasRequiredUrlParams) {
    // if this is the true, it's likely the user came from a bookmarked link
    // and customer has not selected an amount yet, or we lost local state.
    // we need to figure out their status and send them to the right place. (likely select amount screen)
    return <Redirect href="/varo-advance" />;
  }

  return (
    <View
      style={[styles.container, { paddingBottom: bottom + theme.spacing.m }]}
    >
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: title ? title : '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} primaryOnPress={handleBackPress} />
          ),
        }}
      />

      <ScreenTransitionOverlay
        isLoading={isLoading}
        LoadingComponent={
          <LoadingSpinner accessibilityLabel="loading indicator auto-payment" />
        }
        ErrorComponent={
          advanceProductsError || accountOptionsError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('error.title')}
              messageBody={translatedText('error.body')}
              primaryButton={{
                title: translatedText('error.button'),
                onPress: handleBackPress,
              }}
            />
          ) : null
        }
      />

      <>
        {showCalendar ? (
          <View style={styles.calendarContainer}>
            <View style={styles.calendarPickerWrapper}>
              <CalendarPicker
                initialRange={[]}
                minDate={MIN_DATE}
                maxDate={MAX_DATE}
                pastScrollRange={0}
                futureScrollRange={1}
                onDateListener={handleDateSelection}
                holidayList={repaymentDates?.exclusions}
                showDayLabel={false}
              />
            </View>
            <HairLineDivider />
            <BodySmText numberOfLines={0} style={styles.noteContainer}>
              {translatedText('autopayment.note', {
                dueDate: formattedPaymentDueDate,
              })}
            </BodySmText>
            <View>
              <Button
                label={translatedText('autopayment.buttons.calendar')}
                onPress={() => {
                  setShowCalendar(false);
                }}
              />
            </View>
          </View>
        ) : null}

        <ScrollView
          style={styles.contentContainer}
          contentContainerStyle={styles.bodyContainer}
        >
          {!showCalendar ? (
            <>
              <View>
                <View style={styles.paddingLeft}>
                  <TitleXLText
                    numberOfLines={0}
                    style={styles.marginBottomSpacingMedium}
                  >
                    {translatedText('autopayment.title')}
                  </TitleXLText>
                  <BodyBaseText numberOfLines={0} style={styles.marginBottom}>
                    {translatedText('autopayment.bodyV2', {
                      minDate: formattedMinDate,
                      maxDate: formattedMaxDate,
                      dueDate: formattedDueDate,
                    })}
                  </BodyBaseText>
                </View>
                <View testID="payment_source_date_selection_container">
                  <ListItem
                    onPress={() => {
                      showBottomSheet();
                    }}
                    primaryCopy={
                      selectedAccount
                        ? formatAccountName(
                            selectedAccount.name,
                            selectedAccount.maskedAccountNumber || '',
                          )
                        : translatedText('autopayment.paymentMethodPlaceholder')
                    }
                    secondaryCopy={translatedText('autopayment.methodTitle')}
                    analyticsId="accountClicked"
                    lineFormat={ListItemLineFormat.LABEL}
                  />
                  <View style={styles.hairLineDivider}>
                    <HairLineDivider />
                  </View>
                  <ListItem
                    onPress={() => {
                      logEventCalendarIconPressed();
                      setShowCalendar(!showCalendar);
                    }}
                    primaryCopy={
                      selectedDate.displayDate
                        ? selectedDate.displayDate
                        : translatedText('autopayment.datePlaceholder')
                    }
                    secondaryCopy={translatedText('autopayment.dateTitle')}
                    lineFormat={ListItemLineFormat.LABEL}
                    analyticsId="autoPaymentDatePickerClicked"
                    customAttributes={{ amount: selectedAmount }}
                  />
                </View>
                <View style={styles.paddingLeft}>
                  <BodyXSText
                    numberOfLines={0}
                    style={[styles.dateError, styles.marginBottomSpacingMedium]}
                  >
                    {dateError}
                  </BodyXSText>
                  <HyperlinkBaseText
                    numberOfLines={0}
                    onPress={handleZendeskArticle}
                    style={styles.marginTop}
                  >
                    {translatedText('autopayment.linkCopy')}
                  </HyperlinkBaseText>
                </View>
              </View>
              <View style={styles.paddingLeft}>
                <Button
                  label={translatedText('autopayment.buttons.screen')}
                  onPress={handleContinuePress}
                  isDisabled={!canContinue}
                  analyticsId="vaAutoPaymentSubmitted"
                  customAttributes={{
                    amount: selectedAmount,
                    payment_date: selectedDate.displayDate,
                  }}
                />
              </View>
            </>
          ) : null}
        </ScrollView>

        <AdvanceAccountListBottomSheet
          ref={bottomSheetRef}
          accountGroups={accountOptionsData?.options ?? []}
          fetching={isLoading}
          setSelectedAccount={setSelectedAccount}
          showAddPlaid={showLinkAccount ?? false}
          setOpenPlaid={openPlaidLink}
          showAddDebitCard={showLinkDebitCard ?? false}
          setOpenAddDebitCard={setOpenAddDebitCard}
          entryPoint="autoPay"
        />
      </>
    </View>
  );
}

const styles = StyleSheet.create({
  bodyContainer: {
    flexGrow: 1,
    gap: theme.spacing.m,
    justifyContent: 'space-between',
    paddingRight: theme.spacing.m,
  },
  calendarContainer: {
    flexBasis: '100%',
    flexGrow: 1,
    flexShrink: 1,
    paddingHorizontal: theme.spacing.m,
  },
  calendarPickerWrapper: {
    flexBasis: '100%',
    flexGrow: 1,
    flexShrink: 1,
    marginBottom: theme.spacing.xl,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentContainer: {
    flex: 1,
  },
  dateError: {
    color: theme.color.status.negative,
  },
  hairLineDivider: {
    paddingHorizontal: theme.spacing.m,
  },
  marginBottom: {
    marginBottom: theme.spacing.l,
  },
  marginBottomSpacingMedium: {
    marginBottom: theme.spacing.m,
  },
  marginTop: {
    marginTop: theme.spacing.m,
  },
  noteContainer: {
    padding: theme.spacing.m,
  },
  paddingLeft: {
    paddingLeft: theme.spacing.m,
  },
});

export default AutoPaymentChooseScreen;
